import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Backdrop,
  Box,
  Card,
  CardContent,
  Chip,
  Checkbox,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
  Tooltip,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TagOutlinedIcon from '@mui/icons-material/TagOutlined';
import { LoadingButton } from '@mui/lab';
import { Header } from 'components/Header';
import { SelectedService, useServiceConfigs } from '../context/ServiceConfigContext';
import { ServiceConfig } from '../services/types';
import { updateCart, uploadImage, UpdateCartRequest, UploadImageRequest } from '../services/api';
import { formatApiErrorMessage, logApiError } from '../services/errors';
import { v4 as uuidv4 } from 'uuid';
import ServiceDetailHighlights, {
  highlightedLabelsForSelection,
} from '../components/ServiceDetailHighlights';
import { DEFAULT_SERVICE_ICON, getServiceIconAsset } from '../utils/serviceIcons';
import { getServicePricing } from '../utils/servicePricing';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const toAttributeKey = (label: string) =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

/** Big, tappable card for a contact preference (Email / SMS) */
function ContactOptionCard({
  title,
  description,
  icon,
  selected,
  onToggle,
  testId,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  selected: boolean;
  onToggle: (next: boolean) => void;
  testId?: string;
}) {
  return (
    <Paper
      data-testid={testId}
      role="checkbox"
      aria-checked={selected}
      onClick={() => onToggle(!selected)}
      variant="outlined"
      sx={(theme) => ({
        flex: 1,
        p: 2,
        borderRadius: 16,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        borderColor: selected ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.9),
        background:
          selected
            ? alpha(theme.palette.primary.light, theme.palette.mode === 'light' ? 0.16 : 0.28)
            : alpha(theme.palette.background.paper, 0.6),
        transition: 'all .1s ease',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.5),
          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
        },
      })}
    >
      <Box
        sx={(theme) => ({
          width: 42,
          height: 42,
          borderRadius: 12,
          display: 'grid',
          placeItems: 'center',
          background: alpha(theme.palette.primary.main, 0.12),
        })}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography fontWeight={700} sx={{ lineHeight: 1.15 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {description}
        </Typography>
      </Box>
      <Checkbox
        checked={selected}
        tabIndex={-1}
        onChange={(e) => onToggle(e.target.checked)}
        icon={<CheckCircleOutlineIcon sx={{ opacity: 0.3 }} />}
        checkedIcon={<CheckCircleOutlineIcon color="primary" />}
        sx={{ mr: -0.5 }}
      />
    </Paper>
  );
}

interface CustomerOwnedSummaryPageProps {
  externalIdentity: string;
  cartId: string;
}

const CustomerOwnedSummaryPage: React.FC<CustomerOwnedSummaryPageProps> = ({ externalIdentity, cartId }) => {
  const navigate = useNavigate();
  const {
    configs,
    selectedServices,
    setSelectedServices,
    productId,
    serviceType,
    photoUploadInfo,
    setPhotoUploadInfo,
    customer,
    storeId,
    associateId: contextAssociateId,
    associateEmail: contextAssociateEmail,
    associateName: contextAssociateName,
  } = useServiceConfigs();
  const location = useLocation() as {
    state?: { metalType?: string; productType?: string; measuredSize?: string; condition?: string };
  };
  const metalType = location.state?.metalType;
  const productType = location.state?.productType;
  const measuredSize = location.state?.measuredSize;
  const condition = location.state?.condition;
  const resolvedAssociateId = contextAssociateId ?? null;
  const resolvedAssociateEmail = contextAssociateEmail ?? null;
  const resolvedAssociateName = contextAssociateName ?? resolvedAssociateEmail ?? resolvedAssociateId ?? null;

  const [emailContact, setEmailContact] = useState(false);
  const [phoneContact, setPhoneContact] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [snack, setSnack] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isQuick = serviceType === 'STANDARD' ? false : true;

  // Merge selected services with their config
  const addedConfigs = useMemo(() => {
    return selectedServices
      .map((selection) => {
        const cfg = configs?.find((c) => c.id === selection.id);
        return cfg ? { config: cfg, selection } : null;
      })
      .filter((item): item is { config: ServiceConfig; selection: SelectedService } => !!item);
  }, [configs, selectedServices]);

  const totalCost = useMemo(() => {
    return addedConfigs.reduce((sum, { config, selection }) => {
      const { totalPrice } = getServicePricing(selection, config);
      return sum + totalPrice;
    }, 0);
  }, [addedConfigs]);

  const removeService = (lineId: string) => {
    setSelectedServices(selectedServices.filter((s) => s.lineId !== lineId));
  };

  const formatLocation = (location?: string) => {
    if (!location) return '—';
    return location
      .replace(/Kerrville/g, 'Product Care')
      .replace(/Store/g, 'In Store');
  };

  const canSubmit = useMemo(() => {
    if (!addedConfigs.length) return false;
    if (serviceType === 'STANDARD' && !photoUploadInfo.tag?.trim()) return false;
    return true;
  }, [addedConfigs.length, serviceType, photoUploadInfo.tag]);

  const handleSubmit = async () => {
    if (!canSubmit) {
      setSnack('Please complete the required information before submitting.');
      return;
    }

    if (!externalIdentity || !cartId) {
      setSnack('Missing authentication context.');
      return;
    }

    if (!customer?.id) {
      setSnack('Customer information is missing.');
      return;
    }

    if (!photoUploadInfo.photo) {
      setSnack('Photo is required before submitting.');
      return;
    }

    // if (!photoUploadInfo.comment?.trim()) {
    //   setSnack('Please add notes about the item before submitting.');
    //   return;
    // }

    const normalizedTag = photoUploadInfo.tag?.trim().toUpperCase() ?? '';

    if (!isQuick && !normalizedTag) {
      setSnack('Tag number is required for Standard services.');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      let imageId: string | undefined;
      if (photoUploadInfo.photo) {
        const photoFile = photoUploadInfo.photo;
        const uploadImageReq: UploadImageRequest = {
          contentType: photoFile.type || 'application/octet-stream',
          contentLength: photoFile.size,
        };
        const { imageId: uploadedId, uploadUrl } = await uploadImage(externalIdentity, uploadImageReq);

        setUploadProgress(0);
        try {
          await new Promise<void>((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', uploadUrl, true);
            if (photoFile.type) {
              xhr.setRequestHeader('Content-Type', photoFile.type);
            }
            xhr.upload.onprogress = (event) => {
              if (event.lengthComputable) {
                const percent = Math.round((event.loaded / event.total) * 100);
                setUploadProgress(percent);
              } else {
                setUploadProgress((prev) => (prev == null ? 5 : prev));
              }
            };
            xhr.onerror = () => {
              const failureMessage = 'Photo upload failed due to a network error.';
              window.alert(failureMessage);
              reject(new Error(failureMessage));
            };
            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
              } else {
                const failureMessage = `Photo upload failed (${xhr.status} ${xhr.statusText}). ${xhr.responseText || ''}`.trim();
                window.alert(failureMessage);
                console.error('Photo upload failed', {
                  status: xhr.status,
                  statusText: xhr.statusText,
                  body: xhr.responseText,
                });
                reject(new Error(failureMessage));
              }
            };
            xhr.send(photoFile);
          });
          imageId = uploadedId;
        } finally {
          setUploadProgress(null);
        }
      }

      const parentUuid = uuidv4().toString();
      const tagValue = normalizedTag || `QS-${uuidv4().slice(0, 8).toUpperCase()}`;

      if ((!normalizedTag && isQuick) || (normalizedTag && normalizedTag !== photoUploadInfo.tag)) {
        setPhotoUploadInfo({
          photo: photoUploadInfo.photo,
          comment: photoUploadInfo.comment ?? "",
          tag: tagValue,
        });
      }

      const cartAddOns = addedConfigs.flatMap(({ config, selection }) => {
        const baseAttributes = [
          { key: 'parent_product_id', value: productId },
          { key: 'location_type', value: selection.location },
          { key: 'parent_unique_id', value: parentUuid },
          { key: 'service_description', value: selection.description },
          ...(condition ? [{ key: 'condition', value: condition }] : []),
          ...(selection.serviceNote ? [{ key: 'service_note', value: selection.serviceNote }] : []),
        ];

        if (selection.requiredInfoValues) {
          Object.entries(selection.requiredInfoValues).forEach(([label, value]) => {
            if (value == null || value === '') return;
            baseAttributes.push({ key: toAttributeKey(label), value });
          });
        }

        const pricing = getServicePricing(selection, config);
        const createAttributes = () => baseAttributes.map((attr) => ({ ...attr }));

        if (pricing.isEngraving) {
          return [
            {
              productId: selection.sku,
              priceSource: 'internal',
              priceSourceId: pricing.priceSourceId,
              attributes: createAttributes(),
            },
          ];
        }

        const quantity = Math.max(1, selection.quantity || 1);
        return Array.from({ length: quantity }, () => ({
          productId: selection.sku,
          priceSource: 'internal',
          priceSourceId: pricing.priceSourceId,
          attributes: createAttributes(),
        }));
      });

      const updateCartRequest: UpdateCartRequest = {
        productId,
        priceSource: 'internal',
        priceSourceId: 'default',
        attributes: [
          { key: 'integration', value: 'SERVICE_REQUEST' },
          { key: 'unique_id', value: parentUuid },
          { key: 'customer_id', value: customer.id },
          { key: 'customer_name', value: `${customer.firstName} ${customer.lastName}` },
          { key: 'customer_email', value: customer.email ?? '' },
          { key: 'service_type', value: serviceType ?? '' },
          ...(metalType ? [{ key: 'metal', value: metalType }] : []),
          ...(condition ? [{ key: 'condition', value: condition }] : []),
          ...(productType ? [{ key: 'product_type', value: productType }] : []),
          { key: 'measured_size', value: measuredSize ?? 'No Size' },
          { key: 'tag', value: tagValue },
          { key: 'item_location', value: storeId },
          { key: 'item_location_type', value: 'Store' },
          { key: 'contact_by_email', value: emailContact ? 'true' : 'false' },
          // { key: 'contact_by_phone', value: phoneContact ? 'true' : 'false' },
          { key: 'contact_by_phone', value: 'false' },
          ...(imageId ? [{ key: 'image_id', value: imageId }] : []),
          ...(photoUploadInfo.comment ? [{ key: 'image_comment', value: photoUploadInfo.comment }] : []),
          ...(resolvedAssociateId ? [{ key: 'associate_id', value: resolvedAssociateId }] : []),
          ...(resolvedAssociateEmail ? [{ key: 'associate_email', value: resolvedAssociateEmail }] : []),
          ...(resolvedAssociateName ? [{ key: 'associate_name', value: resolvedAssociateName }] : []),
        ],
        cartAddOns,
      };

      await updateCart(externalIdentity, cartId, updateCartRequest);

      // Trigger native app hand-off
      window.open(`com.newstore.associate-one://cart/load?cartId=${cartId}`);
      window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'close' }));
    } catch (err: any) {
      logApiError('Submitting customer-owned summary', err);
      const message = formatApiErrorMessage(err, 'Submission failed. Please try again.');
      setError(message);
      setSnack(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Header
        title={<Typography variant="h6">Selected Services</Typography>}
        subtitle={addedConfigs.length ? `${addedConfigs.length} item${addedConfigs.length === 1 ? '' : 's'}` : undefined}
        showBackButton
        onBack={() => navigate(-1)}
        align="left"
        sticky
        elevateOnScroll
        serviceCount={selectedServices.length}
      />

      {/* Context / Summary */}
      <Card
        sx={(theme) => ({
          mx: 2,
          mt: 2,
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
          boxShadow: '0 6px 18px rgba(0,0,0,0.06)',
          background:
            theme.palette.mode === 'light'
              ? `linear-gradient(180deg, ${alpha(theme.palette.primary.light, 0.18)} 0%, ${alpha(
                theme.palette.background.paper,
                0.9
              )} 100%)`
              : alpha(theme.palette.primary.dark, 0.25),
        })}
      >
        <CardContent sx={{ py: 2.25 }}>
          <Stack spacing={1.25}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip
                label={`Metal: ${metalType ?? '—'}`}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              />
              <Chip
                label={`Product: ${productType ?? '—'}`}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              />
              <Chip
                label={`Condition: ${condition ?? '—'}`}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              />
              <Chip
                label={`Measured Size: ${measuredSize ?? '—'}`}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              />
              <Chip
                icon={<TagOutlinedIcon />}
                label={`Tag: ${photoUploadInfo.tag?.trim() || (isQuick ? 'Will auto-generate' : 'Add before submit')}`}
                variant="outlined"
                color={!photoUploadInfo.tag?.trim() && !isQuick ? 'error' : 'default'}
                sx={{ borderRadius: 2 }}
              />
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip
                color="primary"
                variant="filled"
                icon={<AttachMoneyOutlinedIcon />}
                label={`Total: ${currency.format(totalCost)}`}
                sx={{
                  borderRadius: 2,
                  fontWeight: 700,
                }}
              />
              {!!addedConfigs.length && (
                <Typography variant="caption" color="text.secondary">
                  Taxes calculated at checkout
                </Typography>
              )}
            </Stack>
          </Stack>
        </CardContent>

        <Divider />
        <Box
          sx={(theme) => ({
            px: 2,
            py: 1.25,
            background:
              theme.palette.mode === 'light'
                ? alpha(theme.palette.primary.main, 0.06)
                : alpha(theme.palette.primary.main, 0.18),
          })}
        >
          <Typography variant="caption" color="text.secondary">
            Review your selections. You can remove items or adjust contact preferences below.
          </Typography>
        </Box>
      </Card>

      {/* Services list */}
      <Stack spacing={1.5} sx={{ mx: 2, mt: 2 }}>
        {addedConfigs.length ? (
          addedConfigs.map(({ config, selection }) => {
            const pricing = getServicePricing(selection, config);
            const { effectiveQuantity, totalPrice, isEngraving } = pricing;
            const iconSrc = getServiceIconAsset(config.icon);
            const requiredFields = new Map(
              (config.requiredInfo ?? []).map((field) => [field.label, field])
            );
            const highlightLabels = highlightedLabelsForSelection(selection);
            const infoChips = Object.entries(selection.requiredInfoValues ?? {})
              .map(([label, value]) => {
                if (!value) return null;
                if (highlightLabels.has(label)) return null;
                const field = requiredFields.get(label);
                let displayValue = value;
                if (field?.valueType === 'Boolean') {
                  displayValue = value === 'true' ? 'Yes' : value === 'false' ? 'No' : value;
                }
                return (
                  <Chip
                    key={`${selection.lineId}-${label}`}
                    size="small"
                    variant="outlined"
                    label={`${label}: ${displayValue}`}
                    sx={{ borderRadius: 1.5 }}
                  />
                );
              })
              .filter(Boolean);
            return (
              <Paper
                key={selection.lineId}
                variant="outlined"
                sx={(theme) => ({
                  position: 'relative',
                  p: 1.5,
                  borderRadius: 2,
                  borderColor: alpha(theme.palette.divider, 0.9),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  transition: 'box-shadow .18s ease',
                  '&:hover': { boxShadow: `0 6px 16px ${alpha(theme.palette.common.black, 0.08)}` },
                })}
              >
                <Box
                  sx={(theme) => ({
                    minWidth: 38,
                    minHeight: 38,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                    overflow: 'hidden',
                  })}
                  aria-hidden
                >
                  <Box
                    component="img"
                    src={iconSrc}
                    alt={`${config.description ?? 'Service'} icon`}
                    sx={{ width: 24, height: 24 }}
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = DEFAULT_SERVICE_ICON;
                    }}
                  />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Typography variant="subtitle1" fontWeight={700} noWrap title={config.description}>
                      {config.description}
                    </Typography>
                    <Chip
                      size="small"
                      icon={<AttachMoneyOutlinedIcon />}
                      label={currency.format(totalPrice)}
                      color="primary"
                      variant="outlined"
                      sx={{ borderRadius: 1.5 }}
                    />
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
                    <Chip
                      size="small"
                      variant="outlined"
                      icon={<PlaceOutlinedIcon sx={{ fontSize: 18 }} />}
                      label={formatLocation(selection.location)}
                      sx={{ borderRadius: 1.5 }}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${isEngraving ? 'Chars' : 'Qty'}: ${effectiveQuantity}`}
                      sx={{ borderRadius: 1.5 }}
                    />
                    {infoChips}
                  </Stack>

                  <ServiceDetailHighlights selection={selection} requiredFields={requiredFields} />

                  {selection.serviceNote && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                      Note: {selection.serviceNote}
                    </Typography>
                  )}
                </Box>

                <Tooltip title="Remove service">
                  <IconButton
                    aria-label="Remove service"
                    onClick={() => removeService(selection.lineId)}
                    size="small"
                    sx={(theme) => ({
                      backgroundColor: alpha(theme.palette.error.main, 0.06),
                      color: theme.palette.error.main,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.error.main, 0.12),
                        color: darken(theme.palette.error.main, 0.1),
                      },
                    })}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Paper>
            );
          })
        ) : (
          <Alert
            severity="info"
            variant="outlined"
            sx={(theme) => ({
              mt: 1,
              borderRadius: 2,
              borderColor: alpha(theme.palette.info.main, 0.5),
            })}
          >
            <AlertTitle>No services added</AlertTitle>
            You haven’t added any services yet.
          </Alert>
        )}
      </Stack>

      {/* Contact prefs (STANDARD only) */}
      {serviceType === 'STANDARD' && (
        <Paper
          elevation={0}
          sx={(theme) => ({
            mx: 2,
            mt: 2,
            p: 2,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
            background: alpha(theme.palette.primary.light, 0.05),
          })}
        >
          <Typography variant="subtitle2" gutterBottom fontWeight={700}>
            Contact Preferences
          </Typography>

          <Stack direction="column" spacing={1.25}>
            <ContactOptionCard
              title="Email"
              description="Receive updates in your inbox"
              icon={<EmailOutlinedIcon />}
              selected={emailContact}
              onToggle={setEmailContact}
              testId="contact-email"
            />
            {false && <ContactOptionCard
              title="SMS"
              description="Get quick text notifications"
              icon={<SmsOutlinedIcon />}
              selected={phoneContact}
              onToggle={setPhoneContact}
              testId="contact-sms"
            />}
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.25, display: 'block' }}>
            You can select one or both options.
          </Typography>
        </Paper>
      )}

      {/* Actions */}
      <Box mt={3} mx={2} mb={4}>
        <Stack spacing={1.25}>
          <LoadingButton
            variant="contained"
            fullWidth
            color="primary"
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
            loading={isSubmitting}
            loadingPosition="start"
            startIcon={<CheckCircleIcon />}
            loadingIndicator={<CircularProgress color="inherit" size={20} />}
            sx={(theme) => ({
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              '&.Mui-disabled': {
                color: theme.palette.common.white,
                backgroundColor: alpha(theme.palette.primary.main, 0.5),
              },
            })}
          >
            Submit
          </LoadingButton>
        </Stack>
      </Box>

      <Snackbar
        open={!!snack}
        autoHideDuration={2500}
        onClose={() => setSnack(null)}
        message={snack ?? ''}
      />

      {error && (
        <Box mx={2} mb={4}>
          <Alert severity="error" variant="outlined">
            {error}
          </Alert>
        </Box>
      )}

      <Backdrop
        open={isSubmitting}
        sx={(theme) => ({
          color: theme.palette.common.white,
          zIndex: theme.zIndex.modal + 1,
        })}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress
            color="inherit"
            variant={uploadProgress != null ? 'determinate' : 'indeterminate'}
            value={uploadProgress ?? undefined}
          />
          <Typography variant="body2">
            {uploadProgress != null ? `Uploading photo… ${uploadProgress}%` : 'Submitting request…'}
          </Typography>
        </Stack>
      </Backdrop>
    </Box>
  );
};

export default CustomerOwnedSummaryPage;
